import React, { useState, useMemo, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { startOfMonth } from 'date-fns'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { useIsMobile } from '@/lib/useIsMobile'
import { formatCurrency, formatDate, getStatusColor } from '@/lib/utils'
import { Plus, X, Search, TrendingUp, TrendingDown, DollarSign, Target, Award, ArrowUpDown, Percent } from 'lucide-react'
import { toast } from 'sonner'

function NewDealModal({ onClose, onCreated, stages, contacts, companies, profiles }: {
  onClose: () => void; onCreated: () => void;
  stages: any[]; contacts: any[]; companies: any[]; profiles: any[]
}) {
  const { workspace } = useAuth()
  const [form, setForm] = useState({
    title: '',
    contact_id: '',
    contact_name: '',
    company_id: '',
    value: '',
    stage_id: stages[0]?.id || '',
    priority: 'normal',
    close_date: '',
    owner_id: '',
    owner_name: '',
  })
  const [saving, setSaving] = useState(false)
  const set = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }))

  // If the modal mounted before the `stages` prop resolved, form.stage_id inits
  // to '' and never re-syncs — populate the default once stages arrive (and only
  // when still empty, so we don't override a stage the user already picked).
  useEffect(() => {
    if (stages.length > 0 && !form.stage_id) {
      setForm(p => ({ ...p, stage_id: stages[0].id }))
    }
  }, [stages, form.stage_id])
  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }

  const handleContactSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const c = contacts.find(c => c.id === e.target.value)
    if (c) {
      const name = `${c.first_name || ''} ${c.last_name || ''}`.trim()
      setForm(p => ({ ...p, contact_id: c.id, contact_name: name }))
    } else {
      setForm(p => ({ ...p, contact_id: '', contact_name: '' }))
    }
  }

  const handleOwnerSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const p = profiles.find(p => p.id === e.target.value)
    setForm(prev => ({ ...prev, owner_id: p?.id || '', owner_name: p?.full_name || '' }))
  }

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Deal title is required'); return }
    // Resolve the stage and its name from the SAME row so the two are never
    // half-set (id without name, or vice versa). Fall back to the first stage if
    // form.stage_id is somehow still empty (mount-before-stages-load race).
    const stage = stages.find(s => s.id === form.stage_id) ?? stages[0]
    if (!stage) { toast.error('No pipeline stage available'); return }
    setSaving(true)
    const { error } = await supabase.from('deals').insert({
      title: form.title,
      contact_id: form.contact_id || null,
      contact_name: form.contact_name || null,
      company_id: form.company_id || null,
      value: form.value ? parseFloat(form.value) : null,
      stage_id: stage.id,
      stage_name: stage.name,
      pipeline_id: stage.pipeline_id ?? null,   // paired with the stage (stages are pipeline-scoped) — never half-set
      priority: form.priority,
      close_date: form.close_date || null,
      owner_id: form.owner_id || null,
      owner_name: form.owner_name || null,
      status: 'open',
      currency: 'USD',
      workspace_id: workspace!.id,
    })
    setSaving(false)
    if (error) { toast.error(`Error: ${error.message}`); return }
    toast.success('Deal created')
    onCreated()
    onClose()
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexShrink: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>New deal</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', minHeight: 0 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Deal title *</label>
            <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="Enterprise contract — Acme Corp" style={inp} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Contact</label>
              <select value={form.contact_id} onChange={handleContactSelect} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Select contact...</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name || ''}{c.email ? ` — ${c.email}` : ''}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Company</label>
              <select value={form.company_id} onChange={e => set('company_id', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">No company</option>
                {companies.map(co => <option key={co.id} value={co.id}>{co.name}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Value ($)</label>
              <input type="number" value={form.value} onChange={e => set('value', e.target.value)} placeholder="10000" style={inp} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Close date</label>
              {/* TODO Session 3: theme this — colorScheme: 'dark' pinned for native date picker icons */}
              <input type="date" value={form.close_date} onChange={e => set('close_date', e.target.value)} style={{ ...inp, colorScheme: 'dark' }} onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'} onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Stage</label>
              <select value={form.stage_id} onChange={e => set('stage_id', e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                {stages.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Owner</label>
              <select value={form.owner_id} onChange={handleOwnerSelect} style={{ ...inp, cursor: 'pointer' }}>
                <option value="">Unassigned</option>
                {profiles.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>{saving ? 'Creating...' : 'Create deal'}</button>
        </div>
      </motion.div>
    </motion.div>
  )
}

function DealCard({ deal, onUpdate }: { deal: any; onUpdate: () => void }) {
  const navigate = useNavigate()
  const sc = getStatusColor(deal.priority || 'normal')
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={() => navigate(`/app/deals/${deal.id}`)}
      style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '12px 14px', cursor: 'pointer', transition: 'all 150ms ease' }}
      onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)'; e.currentTarget.style.borderColor = 'var(--accent-border)'; e.currentTarget.style.transform = 'translateY(-1px)' }}
      onMouseLeave={e => { e.currentTarget.style.background = 'var(--surface-raised)'; e.currentTarget.style.borderColor = 'var(--border-subtle)'; e.currentTarget.style.transform = 'translateY(0)' }}
    >
      <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 6px', lineHeight: 1.3 }}>{deal.title}</p>
      {deal.contact_name && (
        <p style={{ fontSize: 11, color: 'var(--ink-tertiary)', margin: '0 0 8px' }}>{deal.contact_name}</p>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {deal.value ? (
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--positive)' }}>{formatCurrency(deal.value)}</span>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>No value</span>
        )}
        {deal.close_date && (
          <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>{formatDate(deal.close_date, 'MMM d')}</span>
        )}
      </div>
      {deal.priority && deal.priority !== 'normal' && (
        <div style={{ marginTop: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 100, background: sc.bg, color: sc.text, textTransform: 'capitalize' }}>{deal.priority}</span>
        </div>
      )}
    </motion.div>
  )
}

const SORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'updated_at_desc', label: 'Recently updated' },
  { value: 'created_at_desc', label: 'Newest first' },
  { value: 'value_desc',      label: 'Value: high → low' },
  { value: 'value_asc',       label: 'Value: low → high' },
  { value: 'close_date_asc',  label: 'Close date soonest' },
  { value: 'title_asc',       label: 'Title A→Z' },
]

// Parse a sort value of the form `<field>_<asc|desc>`. Used by both the dropdown
// (curated values from SORT_OPTIONS) and DealsListView header clicks (any column key).
function parseSort(value: string): { field: string; dir: 'asc' | 'desc' } {
  if (value.endsWith('_desc')) return { field: value.slice(0, -5), dir: 'desc' }
  if (value.endsWith('_asc'))  return { field: value.slice(0, -4), dir: 'asc'  }
  return { field: value, dir: 'desc' }
}

// Single source of truth for loss reason values. Mirrors the CHECK constraint on
// deals.loss_reason added by migration 20260511072443_deals_phase_2_lifecycle.sql.
// Keep these in sync — generated TS types don't narrow CHECK constraints into unions.
const LOSS_REASON_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'price',        label: 'Price' },
  { value: 'competitor',   label: 'Competitor' },
  { value: 'timing',       label: 'Timing' },
  { value: 'no_response',  label: 'No response' },
  { value: 'lost_contact', label: 'Lost contact' },
  { value: 'bad_fit',      label: 'Bad fit' },
  { value: 'other',        label: 'Other' },
]
const LOSS_REASON_LABELS: Record<string, string> = Object.fromEntries(
  LOSS_REASON_OPTIONS.map(o => [o.value, o.label])
)

// A stage is treated as "Lost" when win_probability is 0 AND the name signals closure.
// Shared between handleDrop (intercept for modal) and updateMutation auto-flip
// (defensive — handleDrop normally intercepts first).
function isLostStage(stage: any): boolean {
  return stage?.win_probability === 0 && /lost|closed.lost|dead/i.test(stage?.name || '')
}

function LossReasonModal({ dealTitle, onClose, onSave }: {
  dealTitle: string
  onClose: () => void
  onSave: (reason: string, details: string) => void
}) {
  const [reason, setReason] = useState('')
  const [details, setDetails] = useState('')
  const [saving, setSaving] = useState(false)
  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }

  const handleSave = () => {
    if (!reason) return
    setSaving(true)
    onSave(reason, details)
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      style={{ position: 'fixed', inset: 0, background: 'var(--overlay-scrim)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}
      onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.15 }}
        onClick={e => e.stopPropagation()}
        style={{ background: 'var(--surface)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 440, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, flexShrink: 0 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: 'var(--ink-primary)', margin: 0 }}>Why was this deal lost?</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 18, height: 18 }} /></button>
        </div>
        <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: '0 0 20px', flexShrink: 0 }}>{dealTitle}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto', minHeight: 0 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Reason *</label>
            <select value={reason} onChange={e => setReason(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">Select reason...</option>
              {LOSS_REASON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Details</label>
            <textarea value={details} onChange={e => setDetails(e.target.value)} rows={3} placeholder="Additional context (optional)..."
              style={{ ...inp, resize: 'none', lineHeight: 1.5 }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-default)'} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 24, flexShrink: 0 }}>
          <button onClick={onClose} className="btn-ghost" style={{ flex: 1, justifyContent: 'center' }}>Cancel</button>
          <button onClick={handleSave} disabled={!reason || saving} className="btn-primary" style={{ flex: 1, justifyContent: 'center' }}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </motion.div>
    </motion.div>
  )
}

function DealsListView({ deals, sort, onSortChange, navigate, showLossReason }: {
  deals: any[]; sort: string; onSortChange: (s: string) => void;
  navigate: ReturnType<typeof useNavigate>;
  showLossReason: boolean
}) {
  const isMobile = useIsMobile()
  // loss_reason column is appended only when viewing Lost deals — keeps the
  // default columns aligned for Open/Won/All filters.
  const cols: Array<{ key: string; label: string; align: 'left' | 'right' }> = [
    { key: 'title',       label: 'Title',       align: 'left'  },
    { key: 'stage_name',  label: 'Stage',       align: 'left'  },
    { key: 'value',       label: 'Value',       align: 'right' },
    { key: 'probability', label: 'Probability', align: 'right' },
    { key: 'owner_name',  label: 'Owner',       align: 'left'  },
    { key: 'close_date',  label: 'Close date',  align: 'left'  },
    { key: 'updated_at',  label: 'Updated',     align: 'left'  },
    ...(showLossReason ? [{ key: 'loss_reason', label: 'Loss reason', align: 'left' as const }] : []),
  ]
  const gridCols = showLossReason
    ? '1fr 130px 110px 110px 140px 110px 110px 140px'
    : '1fr 130px 110px 110px 140px 110px 110px'
  const { field: sortField, dir: sortDir } = parseSort(sort)

  const handleHeaderClick = (key: string) => {
    if (sortField === key) {
      onSortChange(`${key}_${sortDir === 'asc' ? 'desc' : 'asc'}`)
    } else {
      const numericFields = ['value', 'probability', 'updated_at', 'close_date', 'created_at']
      const defaultDir = numericFields.includes(key) ? 'desc' : 'asc'
      onSortChange(`${key}_${defaultDir}`)
    }
  }

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, overflow: 'hidden' }}>
      {/* Column header — desktop only. Cards on mobile are self-labeling
          and lose the in-row sort affordance; mobile users can sort via
          the page-level sort dropdown which remains the source of truth. */}
      {!isMobile && (
      <div style={{ display: 'grid', gridTemplateColumns: gridCols, padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface)' }}>
        {cols.map(c => (
          <button key={c.key} onClick={() => handleHeaderClick(c.key)}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 11, fontWeight: 700, color: sortField === c.key ? 'var(--accent-text)' : 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start', gap: 4 }}>
            {c.label}
            {sortField === c.key && <ArrowUpDown style={{ width: 11, height: 11, transform: sortDir === 'asc' ? 'rotate(180deg)' : 'none' }} />}
          </button>
        ))}
      </div>
      )}
      {deals.map((d: any, i: number) => {
        const isLast = i === deals.length - 1

        // Mobile card. Deal title (primary) + value badge top-right
        // (money is the headline for deals). Stage + probability +
        // owner stacked as secondary lines. Close date + updated date
        // grouped as meta. Loss reason appended when viewing Lost.
        if (isMobile) {
          return (
            <div key={d.id} onClick={() => navigate(`/app/deals/${d.id}`)}
              style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 16px', borderBottom: isLast ? 'none' : '1px solid var(--border-subtle)', cursor: 'pointer', transition: 'background 150ms ease' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', margin: 0, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</p>
                <span style={{ fontSize: 14, fontWeight: 700, color: d.value ? 'var(--positive)' : 'var(--ink-tertiary)', flexShrink: 0 }}>
                  {d.value ? formatCurrency(d.value) : '—'}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>
                  {d.stage_name || 'No stage'}{d.probability != null ? ` · ${d.probability}%` : ''}
                </span>
                {d.owner_name && (
                  <span style={{ fontSize: 12, color: 'var(--ink-tertiary)' }}>Owner: {d.owner_name}</span>
                )}
                <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', marginTop: 2 }}>
                  {d.close_date ? `Closes ${formatDate(d.close_date, 'MMM d')} · ` : ''}Updated {d.updated_at ? formatDate(d.updated_at, 'MMM d') : '—'}
                </span>
                {showLossReason && d.loss_reason && (
                  <span style={{ fontSize: 12, color: 'var(--ink-secondary)', fontStyle: 'italic' }}>
                    Lost: {LOSS_REASON_LABELS[d.loss_reason] || d.loss_reason}
                  </span>
                )}
              </div>
            </div>
          )
        }

        // Desktop grid row — unchanged.
        return (
          <div key={d.id} onClick={() => navigate(`/app/deals/${d.id}`)}
            style={{ display: 'grid', gridTemplateColumns: gridCols, padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', alignItems: 'center', fontSize: 13, color: 'var(--ink-primary)', transition: 'background 150ms ease' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'var(--hover-bg)' }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}>
            <div style={{ fontWeight: 600, color: 'var(--ink-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 12 }}>{d.title}</div>
            <div style={{ color: 'var(--ink-secondary)', fontSize: 12 }}>{d.stage_name || '—'}</div>
            <div style={{ textAlign: 'right', fontWeight: 700, color: d.value ? 'var(--positive)' : 'var(--ink-tertiary)' }}>{d.value ? formatCurrency(d.value) : '—'}</div>
            <div style={{ textAlign: 'right', color: 'var(--ink-secondary)', fontSize: 12 }}>{d.probability != null ? `${d.probability}%` : '—'}</div>
            <div style={{ color: 'var(--ink-secondary)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.owner_name || '—'}</div>
            <div style={{ color: 'var(--ink-secondary)', fontSize: 12 }}>{d.close_date ? formatDate(d.close_date, 'MMM d') : '—'}</div>
            <div style={{ color: 'var(--ink-tertiary)', fontSize: 12 }}>{d.updated_at ? formatDate(d.updated_at, 'MMM d') : '—'}</div>
            {showLossReason && (
              <div style={{ color: 'var(--ink-secondary)', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.loss_reason ? LOSS_REASON_LABELS[d.loss_reason] || d.loss_reason : '—'}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default function Deals() {
  const { workspace } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showNewModal, setShowNewModal] = useState(false)
  const [dragOver, setDragOver] = useState<string | null>(null)
  const [view, setView] = useState<'kanban' | 'list'>('kanban')
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'won' | 'lost'>('open')
  const [ownerFilter, setOwnerFilter] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('updated_at_desc')
  // Drag-to-Lost is intercepted: stash the move and open the modal. The mutation
  // doesn't fire until the operator picks a reason (or cancels). Kanban only
  // reflects the move after invalidation, so no optimistic flicker on cancel.
  const [pendingLostMove, setPendingLostMove] = useState<{ dealId: string; dealTitle: string; stageId: string; stageName: string } | null>(null)

  // Multi-pipeline CP3: the workspace's pipelines + the selected one. Effective
  // selection is derived (no useEffect): explicit pick → default → first.
  const [selectedPid, setSelectedPid] = useState<string>('')
  const { data: pipelines = [] } = useQuery({
    queryKey: ['pipelines', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('pipelines').select('id,name,is_default').eq('workspace_id', workspace.id).order('is_default', { ascending: false }).order('created_at')
      return data || []
    },
    enabled: !!workspace?.id,
  })
  const effectivePid: string = selectedPid || (pipelines.find((p: any) => p.is_default)?.id) || (pipelines[0]?.id) || ''

  // Stages = the SELECTED pipeline's stages (board columns + NewDealModal stage list).
  const { data: stages = [] } = useQuery({
    queryKey: ['pipeline-stages', effectivePid],
    queryFn: async () => {
      if (!effectivePid) return []
      const { data } = await supabase.from('pipeline_stages').select('*').eq('pipeline_id', effectivePid).order('order_index')
      return data || []
    },
    enabled: !!effectivePid,
  })

  const { data: deals = [], isLoading } = useQuery({
    queryKey: ['deals', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data, error } = await supabase.from('deals').select('*').eq('workspace_id', workspace.id).order('created_at', { ascending: false })
      if (error) throw error
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: contacts = [] } = useQuery({
    queryKey: ['contacts', 'typeahead-deals', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('contacts').select('id, first_name, last_name, email').eq('workspace_id', workspace.id).order('first_name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: companies = [] } = useQuery({
    queryKey: ['companies-list', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('companies').select('id, name').eq('workspace_id', workspace.id).order('name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const { data: profiles = [] } = useQuery({
    queryKey: ['profiles-list', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return []
      const { data } = await supabase.from('profiles').select('id, full_name').eq('workspace_id', workspace.id).order('full_name')
      return data || []
    },
    enabled: !!workspace?.id,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, stage_id, stage_name, status, loss_reason, loss_reason_details }: {
      id: string
      stage_id: string
      stage_name: string
      status?: 'open' | 'won' | 'lost'
      loss_reason?: string
      loss_reason_details?: string
    }) => {
      // Auto-flip status on terminal-stage drops (Won/Lost). The log_deal_activity
      // Postgres trigger emits won/lost activities atomically when status changes,
      // so no client-side activity emission is needed here.
      const destStage = stages.find((s: any) => s.id === stage_id)
      const currentDeal = deals.find((d: any) => d.id === id)
      const updates: Record<string, any> = { stage_id, stage_name }

      // Caller-supplied status wins — used by the LossReasonModal save path.
      // Otherwise derive from the destination stage.
      if (status) {
        updates.status = status
      } else if (destStage?.win_probability === 100) {
        updates.status = 'won'
      } else if (isLostStage(destStage)) {
        updates.status = 'lost'
      } else if (currentDeal?.status !== 'open') {
        // Inverse flip: dragging out of a terminal stage back to non-terminal restores 'open'.
        updates.status = 'open'
      }

      // Normalize empty strings to NULL — both columns are nullable TEXT, and
      // '' would defeat IS NULL checks elsewhere.
      if (loss_reason !== undefined) updates.loss_reason = loss_reason || null
      if (loss_reason_details !== undefined) updates.loss_reason_details = loss_reason_details || null

      const { error } = await supabase.from('deals').update(updates).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['deals'] }); toast.success('Deal moved') },
    onError: () => toast.error('Failed to move deal'),
  })

  const filteredDeals = useMemo(() => {
    let list = [...deals]
    if (statusFilter !== 'all') list = list.filter((d: any) => d.status === statusFilter)
    if (ownerFilter !== 'all') list = list.filter((d: any) => d.owner_id === ownerFilter)
    if (search) {
      const q = search.toLowerCase()
      list = list.filter((d: any) => d.title?.toLowerCase().includes(q) || d.contact_name?.toLowerCase().includes(q))
    }
    const { field, dir } = parseSort(sort)
    list.sort((a: any, b: any) => {
      const av = a[field], bv = b[field]
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      const cmp = av < bv ? -1 : av > bv ? 1 : 0
      return dir === 'desc' ? -cmp : cmp
    })
    return list
  }, [deals, statusFilter, ownerFilter, search, sort])

  // Kanban shows only open deals (won/lost don't belong on the board) regardless
  // of status filter — operators expect kanban to be the active-pipeline view.
  // List view honors the status filter directly via filteredDeals.
  const kanbanDeals = useMemo(() => {
    // Scope the board to the selected pipeline (CP3). Deals all carry pipeline_id
    // post-CP1 backfill; any stray null-pipeline deal simply doesn't appear on a
    // pipeline board (it still shows in the list view, which isn't pipeline-scoped).
    let list = deals.filter((d: any) => d.status === 'open' && d.pipeline_id === effectivePid)
    if (ownerFilter !== 'all') list = list.filter((d: any) => d.owner_id === ownerFilter)
    if (search) {
      const q = search.toLowerCase()
      list = list.filter((d: any) => d.title?.toLowerCase().includes(q) || d.contact_name?.toLowerCase().includes(q))
    }
    return list
  }, [deals, ownerFilter, search, effectivePid])

  const dealsByStage = useMemo(() => {
    const map: Record<string, any[]> = {}
    stages.forEach((s: any) => { map[s.id] = [] })
    kanbanDeals.forEach((d: any) => {
      if (d.stage_id && map[d.stage_id]) map[d.stage_id].push(d)
      else if (stages.length > 0) {
        const firstStageId = stages[0].id
        if (!map[firstStageId]) map[firstStageId] = []
        map[firstStageId].push(d)
      }
    })
    return map
  }, [kanbanDeals, stages])

  const openDeals = useMemo(() => deals.filter((d: any) => d.status === 'open'), [deals])
  const totalValue = useMemo(() => openDeals.reduce((s: number, d: any) => s + (d.value || 0), 0), [openDeals])
  const lostThisMonth = useMemo(() => {
    const monthStart = startOfMonth(new Date())
    return deals.filter((d: any) => d.status === 'lost' && new Date(d.updated_at) >= monthStart).length
  }, [deals])
  // Win rate this month: won / (won + lost) over deals closed in the current month.
  // Returns null when no deals have closed yet — display shows '—' in that case
  // rather than 0% (which would imply a meaningful zero).
  const winRateThisMonth = useMemo(() => {
    const monthStart = startOfMonth(new Date())
    const closedThisMonth = deals.filter((d: any) =>
      (d.status === 'won' || d.status === 'lost') && new Date(d.updated_at) >= monthStart
    )
    if (closedThisMonth.length === 0) return null
    const wonCount = closedThisMonth.filter((d: any) => d.status === 'won').length
    return (wonCount / closedThisMonth.length) * 100
  }, [deals])

  const handleDragStart = (e: React.DragEvent, dealId: string) => {
    e.dataTransfer.setData('dealId', dealId)
  }

  const handleDrop = (e: React.DragEvent, stageId: string, stageName: string) => {
    e.preventDefault()
    setDragOver(null)
    const dealId = e.dataTransfer.getData('dealId')
    if (!dealId) return
    const destStage = stages.find((s: any) => s.id === stageId)
    if (isLostStage(destStage)) {
      const dealRow = deals.find((d: any) => d.id === dealId)
      setPendingLostMove({ dealId, dealTitle: dealRow?.title || '', stageId, stageName })
      return
    }
    updateMutation.mutate({ id: dealId, stage_id: stageId, stage_name: stageName })
  }

  const handleLossSave = (reason: string, details: string) => {
    if (!pendingLostMove) return
    updateMutation.mutate({
      id: pendingLostMove.dealId,
      stage_id: pendingLostMove.stageId,
      stage_name: pendingLostMove.stageName,
      status: 'lost',
      loss_reason: reason,
      loss_reason_details: details,
    })
    setPendingLostMove(null)
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>

      {/* Header */}
      <div style={{ padding: '24px 28px 20px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 900, color: 'var(--ink-primary)', letterSpacing: '-0.03em', margin: 0 }}>Deals</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: '3px 0 0' }}>
              {openDeals.length} open · {formatCurrency(totalValue)} pipeline
            </p>
          </div>
          <button onClick={() => setShowNewModal(true)} className="btn-primary">
            <Plus style={{ width: 15, height: 15 }} /> New deal
          </button>
        </div>

        {/* Stats — auto-fit so cards reflow on narrow viewports instead
            of compressing 5-up to unreadable widths on mobile. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 16 }}>
          {[
            { label: 'Total pipeline', value: formatCurrency(totalValue), icon: DollarSign, color: 'var(--accent)' },
            { label: 'Open deals', value: openDeals.length, icon: TrendingUp, color: 'var(--info)' },
            { label: 'Avg deal size', value: openDeals.length ? formatCurrency(totalValue / openDeals.length) : '$0', icon: Target, color: 'var(--positive)' },
            { label: 'Win rate this month', value: winRateThisMonth == null ? '—' : `${winRateThisMonth.toFixed(1)}%`, icon: Percent, color: 'var(--warning)' },
            { label: 'Lost this month', value: lostThisMonth, icon: TrendingDown, color: 'var(--danger)' },
          ].map(stat => (
            <div key={stat.label} style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, padding: '14px 16px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: -20, right: -20, width: 60, height: 60, background: `radial-gradient(circle, color-mix(in srgb, ${stat.color} var(--status-tint-fill), transparent) 0%, transparent 70%)`, pointerEvents: 'none' }} />
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{stat.label}</span>
                <stat.icon style={{ width: 14, height: 14, color: stat.color }} />
              </div>
              <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--ink-primary)', margin: 0, letterSpacing: '-0.02em' }}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Filters + view toggle */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 260 }}>
            <Search style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, color: 'var(--ink-tertiary)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search deals..."
              style={{ width: '100%', padding: '8px 12px 8px 32px', background: 'var(--field-bg)', border: '1px solid var(--border-subtle)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--focus-ring)'}
              onBlur={e => e.currentTarget.style.borderColor = 'var(--border-subtle)'} />
            {search && <button onClick={() => setSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0 }}><X style={{ width: 12, height: 12 }} /></button>}
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            {(['all', 'open', 'won', 'lost'] as const).map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                style={{ padding: '7px 13px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize', background: statusFilter === s ? 'var(--accent)' : 'var(--hover-bg)', color: statusFilter === s ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: statusFilter === s ? 'none' : '1px solid var(--border-subtle)' }}>
                {s}
              </button>
            ))}
          </div>

          {/* Multi-pipeline CP3: pipeline switcher — hidden for single-pipeline workspaces. */}
          {(pipelines as any[]).length > 1 && (
            <select value={effectivePid} onChange={e => setSelectedPid(e.target.value)}
              style={{ padding: '7px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, fontSize: 12, fontWeight: 600, color: 'var(--ink-primary)', outline: 'none', cursor: 'pointer' }}>
              {(pipelines as any[]).map(p => <option key={p.id} value={p.id}>{p.name}{p.is_default ? ' (default)' : ''}</option>)}
            </select>
          )}

          <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}
            style={{ padding: '7px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, fontSize: 12, color: 'var(--ink-secondary)', outline: 'none', cursor: 'pointer' }}>
            <option value="all">Any owner</option>
            {profiles.map((p: any) => <option key={p.id} value={p.id}>{p.full_name}</option>)}
          </select>

          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto', alignItems: 'center' }}>
            {view === 'list' && (
              <select value={sort} onChange={e => setSort(e.target.value)}
                style={{ padding: '7px 12px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, fontSize: 12, color: 'var(--ink-secondary)', outline: 'none', cursor: 'pointer' }}>
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
            <div style={{ display: 'flex', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden' }}>
              {(['kanban', 'list'] as const).map(v => (
                <button key={v} onClick={() => setView(v)}
                  style={{ padding: '6px 12px', background: view === v ? 'var(--accent)' : 'transparent', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: view === v ? 'var(--accent-ink)' : 'var(--ink-tertiary)', textTransform: 'capitalize' }}>
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Board / List */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px 28px 28px' }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-tertiary)' }}>Loading...</div>
        ) : view === 'list' ? (
          filteredDeals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 24px' }}>
              <TrendingUp style={{ width: 36, height: 36, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
              <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>{search || statusFilter !== 'all' || ownerFilter !== 'all' ? 'No deals match your filters' : 'No deals yet'}</p>
              <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>{search || statusFilter !== 'all' || ownerFilter !== 'all' ? 'Try different filters' : 'Create your first deal to get started'}</p>
            </div>
          ) : (
            <DealsListView deals={filteredDeals} sort={sort} onSortChange={setSort} navigate={navigate} showLossReason={statusFilter === 'lost'} />
          )
        ) : stages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 24px' }}>
            <TrendingUp style={{ width: 36, height: 36, color: 'var(--ink-tertiary)', margin: '0 auto 12px', display: 'block' }} />
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-secondary)', margin: '0 0 6px' }}>No pipeline stages found</p>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>Check your Supabase database — pipeline stages should have been created during signup</p>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 14, height: '100%', minWidth: 'max-content' }}>
            {stages.map((stage: any) => {
              const stageDeals = dealsByStage[stage.id] || []
              const stageValue = stageDeals.reduce((s: number, d: any) => s + (d.value || 0), 0)
              const isDragTarget = dragOver === stage.id

              return (
                <div key={stage.id}
                  onDragOver={e => { e.preventDefault(); setDragOver(stage.id) }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={e => handleDrop(e, stage.id, stage.name)}
                  style={{ width: 260, flexShrink: 0, display: 'flex', flexDirection: 'column', background: isDragTarget ? 'color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent)' : 'var(--surface)', border: `1px solid ${isDragTarget ? 'var(--accent-border)' : 'var(--border-subtle)'}`, borderRadius: 14, overflow: 'hidden', transition: 'all 150ms ease' }}>

                  {/* Column header */}
                  <div style={{ padding: '14px 16px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {/* stage.color is DB user content — treat as raw hex; themed fallback when null */}
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: stage.color || 'var(--accent)' }} />
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)' }}>{stage.name}</span>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-tertiary)', background: 'var(--hover-bg)', padding: '2px 8px', borderRadius: 100 }}>
                        {stageDeals.length}
                      </span>
                    </div>
                    {stageValue > 0 && (
                      <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--positive)', margin: 0 }}>{formatCurrency(stageValue)}</p>
                    )}
                  </div>

                  {/* Cards */}
                  <div style={{ flex: 1, overflow: 'auto', padding: '10px 10px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {stageDeals.map((deal: any) => (
                      <div key={deal.id} draggable onDragStart={e => handleDragStart(e, deal.id)}>
                        <DealCard deal={deal} onUpdate={() => queryClient.invalidateQueries({ queryKey: ['deals'] })} />
                      </div>
                    ))}
                    {stageDeals.length === 0 && !isDragTarget && (
                      <div style={{ padding: '20px 10px', textAlign: 'center' }}>
                        <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>Drop deals here</p>
                      </div>
                    )}
                    {isDragTarget && (
                      <div style={{ padding: '16px 10px', border: '2px dashed var(--accent-border)', borderRadius: 10, textAlign: 'center' }}>
                        <p style={{ fontSize: 12, color: 'var(--accent-text)', margin: 0, fontWeight: 600 }}>Drop here</p>
                      </div>
                    )}
                  </div>

                  {/* Add deal button */}
                  <button onClick={() => setShowNewModal(true)}
                    style={{ margin: '0 10px 10px', padding: '8px', background: 'none', border: '1px dashed var(--border-default)', borderRadius: 9, color: 'var(--ink-tertiary)', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, transition: 'all 150ms ease' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-border)'; e.currentTarget.style.color = 'var(--accent-text)' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.color = 'var(--ink-tertiary)' }}>
                    <Plus style={{ width: 13, height: 13 }} /> Add deal
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showNewModal && (
          <NewDealModal
            onClose={() => setShowNewModal(false)}
            onCreated={() => queryClient.invalidateQueries({ queryKey: ['deals'] })}
            stages={stages}
            contacts={contacts}
            companies={companies}
            profiles={profiles}
          />
        )}
        {pendingLostMove && (
          <LossReasonModal
            dealTitle={pendingLostMove.dealTitle}
            onClose={() => setPendingLostMove(null)}
            onSave={handleLossSave}
          />
        )}
      </AnimatePresence>
    </div>
  )
}